import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Calendar, Package, Camera, AlertTriangle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";

interface OrderItem {
  product_name: string;
  quantity: number;
  unit_price_cents: number;
}

interface OrderDetails {
  id: string;
  customer_name: string;
  delivery_date: string;
  total_cents: number;
  service_type: string;
  is_asap_delivery: boolean;
  items: OrderItem[];
}

interface OrderSuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
  memorialName?: string;
}

export const OrderSuccessDialog: React.FC<OrderSuccessDialogProps> = ({
  open,
  onOpenChange,
  orderId,
  memorialName,
}) => {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) return;

      setIsLoading(true);
      try {
        // Route through the get-order-confirmation edge function. RLS on
        // `orders` blocks anonymous reads of non-delivered orders, so direct
        // table queries fail right after checkout in an anonymous/guest
        // session. The edge function uses service-role to read and treats
        // the order UUID as the capability token.
        const { data, error } = await supabase.functions.invoke("get-order-confirmation", {
          body: { orderId },
        });

        if (error) throw error;
        if (!data?.order) throw new Error("Order not found");

        setOrder(data.order as OrderDetails);
      } catch (error) {
        console.error("Error fetching order details:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open && orderId) {
      fetchOrderDetails();
    }
  }, [open, orderId]);

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-brand-background max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">Order Confirmed</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-accent" />
          </div>
        ) : order ? (
          <div className="space-y-6">
            {/* Success Icon */}
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4 animate-scale-in">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-display font-semibold text-brand-primary">
                Order Confirmed!
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Order #{order.id.slice(0, 8).toUpperCase()}
              </p>
              {memorialName && (
                <p className="text-sm text-muted-foreground">
                  For {memorialName}
                </p>
              )}
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg p-4 border border-border">
              <h3 className="font-medium text-brand-primary mb-3">Order Summary</h3>
              <div className="space-y-2">
                {order.items.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {item.quantity}x {item.product_name}
                    </span>
                    <span className="font-medium">
                      {formatPrice(item.unit_price_cents * item.quantity)}
                    </span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                  <span>Total</span>
                  <span className="text-brand-accent">
                    {formatPrice(order.total_cents)}
                  </span>
                </div>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="bg-brand-accent/10 rounded-lg p-4 border border-brand-accent/20">
              <div className="flex items-center gap-2 text-brand-accent">
                <Calendar className="w-4 h-4" />
                <span className="font-medium">
                  {order.is_asap_delivery
                    ? "ASAP - Drop off within 24 hours"
                    : `Scheduled: ${format(parseISO(order.delivery_date), "MMMM d, yyyy")}`}
                </span>
              </div>
            </div>

            {/* Cancellation Policy */}
            {order.is_asap_delivery ? (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    ASAP orders are final and cannot be cancelled.
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground">
                    <p>
                      Need to cancel? You can cancel this order free of charge up to 24 hours before your scheduled delivery date.
                    </p>
                    <a
                      href={`/cancel-order?orderId=${order.id}`}
                      className="text-brand-accent hover:underline font-medium mt-1 inline-block"
                    >
                      Cancel Order →
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* What Happens Next */}
            <div>
              <h3 className="font-medium text-brand-primary mb-3">What happens next?</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Package className="w-3 h-3 text-brand-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Partner assignment</p>
                    <p className="text-xs text-muted-foreground">
                      A local partner near the gravesite is being assigned
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Calendar className="w-3 h-3 text-brand-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Gravesite visit</p>
                    <p className="text-xs text-muted-foreground">
                      They'll visit the gravesite to complete your order
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-brand-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Camera className="w-3 h-3 text-brand-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Photo confirmation</p>
                    <p className="text-xs text-muted-foreground">
                      You'll receive an email with a photo when delivered
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <Button
              onClick={() => onOpenChange(false)}
              className="w-full bg-brand-accent hover:bg-brand-accent/90"
            >
              Back to Memorial
            </Button>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Unable to load order details
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
